import type {MonaCanvasBounds} from '../../MonaCanvas'

export const COLUMN_COUNT = 25
export const ROW_COUNT = 8

export type MonaDividerGridFocusRange = Readonly<{
  firstColumn: number
  firstRow: number
  focusedCellKeys: ReadonlySet<string>
  lastColumn: number
  lastRow: number
}>

export function getMonaDividerGridCellKey(x: number, y: number) {
  return `${x}-${y}`
}

export function getMonaDividerGridFocusRange(bounds: MonaCanvasBounds | null): MonaDividerGridFocusRange | null {
  if (!bounds) return null

  const FOCUS_RADIUS_RATIO = 0.4
  const GRID_CENTER_TOLERANCE = 1e-9

  const horizontalFocusDistance = bounds.width * FOCUS_RADIUS_RATIO + 0.5 / COLUMN_COUNT
  const verticalFocusDistance = bounds.height * FOCUS_RADIUS_RATIO + 0.5 / ROW_COUNT
  const firstColumn = Math.max(
    0,
    Math.ceil((bounds.centerX - horizontalFocusDistance) * COLUMN_COUNT - 0.5 - GRID_CENTER_TOLERANCE),
  )
  const lastColumn = Math.min(
    COLUMN_COUNT - 1,
    Math.floor((bounds.centerX + horizontalFocusDistance) * COLUMN_COUNT - 0.5 + GRID_CENTER_TOLERANCE),
  )
  const firstRow = Math.max(
    0,
    Math.ceil((bounds.centerY - verticalFocusDistance) * ROW_COUNT - 0.5 - GRID_CENTER_TOLERANCE),
  )
  const lastRow = Math.min(
    ROW_COUNT - 1,
    Math.floor((bounds.centerY + verticalFocusDistance) * ROW_COUNT - 0.5 + GRID_CENTER_TOLERANCE),
  )

  if (firstColumn > lastColumn || firstRow > lastRow) return null

  const focusedCellKeys = new Set<string>()
  for (let row = firstRow; row <= lastRow; row++) {
    for (let column = firstColumn; column <= lastColumn; column++) {
      const cellCenterX = (column + 0.5) / COLUMN_COUNT
      const cellCenterY = (row + 0.5) / ROW_COUNT
      const normalizedHorizontalDistance = (cellCenterX - bounds.centerX) / horizontalFocusDistance
      const normalizedVerticalDistance = (cellCenterY - bounds.centerY) / verticalFocusDistance
      const distanceFromMonaCenter = Math.hypot(normalizedHorizontalDistance, normalizedVerticalDistance)

      if (distanceFromMonaCenter <= 1 + GRID_CENTER_TOLERANCE) {
        focusedCellKeys.add(getMonaDividerGridCellKey(column, row))
      }
    }
  }

  return {
    firstColumn,
    firstRow,
    focusedCellKeys,
    lastColumn,
    lastRow,
  }
}

function areSetsEqual<T>(first: ReadonlySet<T>, second: ReadonlySet<T>) {
  return first.size === second.size && [...first].every(value => second.has(value))
}

export function areMonaDividerGridFocusRangesEqual(
  first: MonaDividerGridFocusRange | null,
  second: MonaDividerGridFocusRange | null,
) {
  return (
    first === second ||
    (first !== null &&
      second !== null &&
      first.firstColumn === second.firstColumn &&
      first.firstRow === second.firstRow &&
      areSetsEqual(first.focusedCellKeys, second.focusedCellKeys) &&
      first.lastColumn === second.lastColumn &&
      first.lastRow === second.lastRow)
  )
}
